import type { MapProject } from '../../common/types/projectv2';
import type { ProjectTabs } from '../ProjectListControls';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import styles from '../ProjectsSection.module.scss';
import NoDataFound from '../../../../public/assets/images/icons/projectV2/NoDataFound';
import { Virtuoso } from 'react-virtuoso';
import ProjectSnippet from '../ProjectSnippet';
import LiveRegion from '../../common/LiveRegion';
import { useProjectStore } from '../../../stores';
import { useFilteredProjects } from '../../../hooks/useFilteredProjects';

interface ProjectListProps {
  tabSelected: ProjectTabs;
}

const ProjectList = ({ tabSelected }: ProjectListProps) => {
  const tAllProjects = useTranslations('AllProjects');
  const { filteredProjects } = useFilteredProjects();
  // store: state
  const projects = useProjectStore((state) => state.projects);
  const topProjects = useProjectStore((state) => state.topProjects);
  const showDonatableProjects = useProjectStore(
    (state) => state.showDonatableProjects
  );
  const debouncedSearchValue = useProjectStore(
    (state) => state.debouncedSearchValue
  );
  const isClassificationSelected = useProjectStore(
    (state) => state.selectedClassification.length > 0
  );

  const projectsToDisplay = useMemo(() => {
    const hasFilterOrSearchApplied =
      debouncedSearchValue !== '' ||
      isClassificationSelected ||
      showDonatableProjects;
    if (hasFilterOrSearchApplied) return filteredProjects;
    return tabSelected === 'topProjects' ? topProjects : projects;
  }, [
    filteredProjects,
    tabSelected,
    debouncedSearchValue,
    isClassificationSelected,
    showDonatableProjects,
    projects,
    topProjects,
  ]);

  const sortedProjects = useMemo(() => {
    return [...(projectsToDisplay ?? [])].sort((a, b) => {
      if (a.properties.allowDonations === b.properties.allowDonations) return 0;
      return a.properties.allowDonations ? -1 : 1;
    });
  }, [projectsToDisplay]);

  const renderProjectSnippet = useCallback(
    (project: MapProject) => (
      <ProjectSnippet
        key={project.properties.id}
        project={project.properties}
        showTooltipPopups={true}
      />
    ),
    []
  );

  const isProjectFound = !(projectsToDisplay?.length === 0);

  return (
    <>
      {/* This is a `polite` message because "No projects found" is the result of the
    user's search or filter, not an error.

    Keep the live region outside the conditional rendering so it stays on the
    page. When the text changes from empty to "No projects found", screen
    readers announce it more reliably than if the live region is added together
    with the message.

    The live region is visually hidden, so it doesn't affect the visible layout
    or the empty state UI. */}
      <LiveRegion politeness="polite" isVisuallyHidden>
        {isProjectFound ? '' : tAllProjects('noProjectFound')}
      </LiveRegion>
      {!isProjectFound ? (
        <div className={styles.projectList}>
          <div className={styles.noProjectFoundContainer}>
            <NoDataFound />
            <p className={styles.noProjectFoundText}>
              {tAllProjects('noProjectFound')}
            </p>
          </div>
        </div>
      ) : (
        // Virtualized so only the cards near the viewport mount (was ~259 at once).
        <Virtuoso
          className={styles.projectList}
          data={sortedProjects ?? []}
          computeItemKey={(_, project) => project.properties.id}
          itemContent={(_, project) => (
            <div style={{ paddingBottom: 12 }}>
              {renderProjectSnippet(project)}
            </div>
          )}
          overscan={600} // px; ~3 cards above and below viewport to avoid sudden appearance while scrolling
          components={{
            Footer: () => <div style={{ height: 53 }} />,
          }}
        />
      )}
    </>
  );
};

export default ProjectList;
